import { useState, type CSSProperties, type ReactNode } from 'react';
import { CylinderEntryRow } from '../components/CylinderEntryRow';
import { Icon } from '../components/Icon';
import { NumberField } from '../components/NumberField';
import { SnapMetric } from '../components/Metric';
import { flaggedMagCheckEntries } from '../lib/readings';
import { useEngineStore } from '../state/engineStore';

type Phase = 'rpm' | 'lean' | 'right' | 'left' | 'review';

const EXPECTED_MIN = 50;
const EXPECTED_MAX = 150;

export function RunupScreen() {
  const {
    readings,
    setCylinderEgt,
    setMagRise,
    resetMagCheck,
  } = useEngineStore();

  const [phase, setPhase] = useState<Phase>('rpm');

  const cylinders = readings.cylinders;
  const magCheck = readings.magCheck;

  const baselineEntered = cylinders.filter((c) => c.egt !== undefined).length;
  const rightEntered = magCheck.filter((m) => m.rightRise !== undefined).length;
  const leftEntered = magCheck.filter((m) => m.leftRise !== undefined).length;

  const flagged = flaggedMagCheckEntries(magCheck, EXPECTED_MIN, EXPECTED_MAX);

  return (
    <div className="col gap-lg">
      <div className="callout info">
        <Icon name="spark" size={18} />
        <div>
          <div className="callout-title">Why lean before the run-up?</div>
          <div>
            At idle on the ground the engine runs far too rich. Plugs foul, combustion is
            uneven, and the mag-check EGT rise is not representative. Leaning to near-peak
            before the check gives a clean, meaningful result.
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card card-lg">
          <div className="card-h" style={{ marginBottom: 4 }}>
            1,800 RPM run-up
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>
            Configure, lean, mag-check per side, enter each cylinder's EGT rise, back to
            full rich before take-off.
          </div>

          <div className="col gap-sm">
            <RunupStep
              n="1"
              title="Set 1,800 RPM"
              done={phase !== 'rpm'}
              active={phase === 'rpm'}
              detail="Throttle set, brakes on, controls neutral."
              action={
                phase === 'rpm' && (
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => setPhase('lean')}
                  >
                    Confirm
                  </button>
                )
              }
            />
            <RunupStep
              n="2"
              title="Lean to near-peak"
              done={
                phase === 'right' ||
                phase === 'left' ||
                phase === 'review'
              }
              active={phase === 'lean'}
              detail="Mixture in until roughness or peak EGT, then enrich 10–20 °F. Enter each cylinder's EGT below."
              action={
                phase === 'lean' &&
                baselineEntered >= cylinders.length && (
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => setPhase('right')}
                  >
                    Next: right mag
                  </button>
                )
              }
            />
            <RunupStep
              n="3"
              title="Switch to R mag"
              done={phase === 'left' || phase === 'review'}
              active={phase === 'right'}
              detail="Selector R. Enter the EGT rise seen on each cylinder. Switch back to Both before next mag."
              action={
                phase === 'right' &&
                rightEntered >= cylinders.length && (
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => setPhase('left')}
                  >
                    Next: left mag
                  </button>
                )
              }
            />
            <RunupStep
              n="4"
              title="Switch to L mag"
              done={phase === 'review'}
              active={phase === 'left'}
              detail="Selector L. Enter the EGT rise per cylinder. Switch back to Both."
              action={
                phase === 'left' &&
                leftEntered >= cylinders.length && (
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => setPhase('review')}
                  >
                    Review
                  </button>
                )
              }
            />
            <RunupStep
              n="5"
              title="Full rich before take-off"
              done={false}
              active={phase === 'review'}
              detail="Or density-altitude lean for high elevation."
              action={
                phase === 'review' && (
                  <button
                    type="button"
                    className="btn sm accent"
                    onClick={() => {
                      resetMagCheck();
                      setPhase('rpm');
                    }}
                  >
                    Reset & start over
                  </button>
                )
              }
            />
          </div>
        </div>

        <div className="card card-lg">
          {phase === 'rpm' && (
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Set 1,800 RPM first, then the next step unlocks.
            </div>
          )}

          {phase === 'lean' && (
            <div>
              <div className="card-h" style={{ marginBottom: 10 }}>
                Near-peak baseline EGT
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
                Enter each cylinder's EGT after leaning to roughly peak.
              </div>
              <CylinderEntryRow
                cylinders={cylinders}
                field="egt"
                onChange={setCylinderEgt}
              />
            </div>
          )}

          {phase === 'right' && (
            <div>
              <div className="card-h" style={{ marginBottom: 10 }}>
                Right-mag EGT rise
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
                For each cylinder, enter the °F rise you saw when selecting R. Expected
                range: {EXPECTED_MIN}–{EXPECTED_MAX} °F.
              </div>
              <div
                className="cyl-entry-grid"
                style={{ '--cyl-count': cylinders.length } as CSSProperties}
              >
                {magCheck.map((m) => (
                  <MagFieldColumn
                    key={m.id}
                    cylId={m.id}
                    value={m.rightRise}
                    onChange={(v) => setMagRise(m.id, 'right', v)}
                  />
                ))}
              </div>
            </div>
          )}

          {phase === 'left' && (
            <div>
              <div className="card-h" style={{ marginBottom: 10 }}>
                Left-mag EGT rise
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
                For each cylinder, enter the °F rise you saw when selecting L. Expected
                range: {EXPECTED_MIN}–{EXPECTED_MAX} °F.
              </div>
              <div
                className="cyl-entry-grid"
                style={{ '--cyl-count': cylinders.length } as CSSProperties}
              >
                {magCheck.map((m) => (
                  <MagFieldColumn
                    key={m.id}
                    cylId={m.id}
                    value={m.leftRise}
                    onChange={(v) => setMagRise(m.id, 'left', v)}
                  />
                ))}
              </div>
            </div>
          )}

          {phase === 'review' && (
            <div>
              <div className="card-h" style={{ marginBottom: 12 }}>
                Mag-check review
              </div>
              <div className="mag-review-grid">
                <div className="stat-label">Cyl</div>
                <div className="stat-label" style={{ textAlign: 'right' }}>
                  R rise
                </div>
                <div className="stat-label" style={{ textAlign: 'right' }}>
                  L rise
                </div>
                <div className="stat-label" style={{ textAlign: 'right' }}>
                  Δ L−R
                </div>
                {magCheck.map((m) => {
                  const delta =
                    m.leftRise !== undefined && m.rightRise !== undefined
                      ? m.leftRise - m.rightRise
                      : undefined;
                  const rightOk =
                    m.rightRise !== undefined &&
                    m.rightRise >= EXPECTED_MIN &&
                    m.rightRise <= EXPECTED_MAX;
                  const leftOk =
                    m.leftRise !== undefined &&
                    m.leftRise >= EXPECTED_MIN &&
                    m.leftRise <= EXPECTED_MAX;
                  return (
                    <ReviewRow
                      key={m.id}
                      cylId={m.id}
                      right={m.rightRise}
                      rightOk={rightOk}
                      left={m.leftRise}
                      leftOk={leftOk}
                      delta={delta}
                    />
                  );
                })}
              </div>

              <div style={{ marginTop: 14 }}>
                {flagged.length === 0 ? (
                  <div className="callout info">
                    <Icon name="check" size={18} />
                    <div>
                      <div className="callout-title">All rises inside expected band</div>
                      <div>
                        Every cylinder rose {EXPECTED_MIN}–{EXPECTED_MAX} °F on both
                        magnetos. No obvious plug or lead problem.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="callout warn">
                    <Icon name="alert" size={18} />
                    <div>
                      <div className="callout-title">
                        {flagged.length} cylinder{flagged.length === 1 ? '' : 's'} outside
                        expected range
                      </div>
                      <div>
                        Cyl{' '}
                        {flagged.map((m) => `#${m.id}`).join(', ')} shows a rise below{' '}
                        {EXPECTED_MIN} °F or above {EXPECTED_MAX} °F — investigate plug,
                        lead or wire before departure.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {(phase === 'right' || phase === 'left' || phase === 'review') && (
        <div className="card">
          <div className="card-h" style={{ marginBottom: 10 }}>
            Mag-check progress
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16,
            }}
          >
            <SnapMetric
              label="R entries"
              value={`${rightEntered} / ${cylinders.length}`}
            />
            <SnapMetric
              label="L entries"
              value={`${leftEntered} / ${cylinders.length}`}
            />
            <SnapMetric label="Flagged" value={flagged.length} kind={flagged.length ? 'warn' : 'ok'} />
            <SnapMetric label="Expected range" value={`${EXPECTED_MIN}–${EXPECTED_MAX}`} unit="°F" />
          </div>
        </div>
      )}
    </div>
  );
}

type MagFieldColumnProps = {
  cylId: number;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
};

function MagFieldColumn({ cylId, value, onChange }: MagFieldColumnProps) {
  const flagged =
    value !== undefined && (value < EXPECTED_MIN || value > EXPECTED_MAX);
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: flagged ? 'var(--warn)' : 'var(--ink-3)',
          fontWeight: 600,
          marginBottom: 4,
          textAlign: 'center',
        }}
      >
        #{cylId}
      </div>
      <NumberField
        value={value}
        onChange={onChange}
        align="center"
        min={0}
        max={400}
        placeholder="°F"
        ariaLabel={`Cylinder ${cylId} mag rise`}
      />
    </div>
  );
}

type ReviewRowProps = {
  cylId: number;
  right: number | undefined;
  rightOk: boolean;
  left: number | undefined;
  leftOk: boolean;
  delta: number | undefined;
};

function ReviewRow({ cylId, right, rightOk, left, leftOk, delta }: ReviewRowProps) {
  const rightColor = right === undefined ? 'var(--ink-3)' : rightOk ? 'var(--ok)' : 'var(--warn)';
  const leftColor = left === undefined ? 'var(--ink-3)' : leftOk ? 'var(--ok)' : 'var(--warn)';
  const deltaColor =
    delta === undefined
      ? 'var(--ink-3)'
      : Math.abs(delta) > 75
        ? 'var(--warn)'
        : 'var(--ink)';
  return (
    <>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--ink-3)' }}>
        #{cylId}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          textAlign: 'right',
          color: rightColor,
        }}
      >
        {right !== undefined ? `+${Math.round(right)} °F` : '—'}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          textAlign: 'right',
          color: leftColor,
        }}
      >
        {left !== undefined ? `+${Math.round(left)} °F` : '—'}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          textAlign: 'right',
          color: deltaColor,
        }}
      >
        {delta !== undefined
          ? `${delta > 0 ? '+' : ''}${Math.round(delta)} °F`
          : '—'}
      </div>
    </>
  );
}

type RunupStepProps = {
  n: string;
  title: string;
  detail: string;
  done?: boolean;
  active?: boolean;
  action?: ReactNode;
};

function RunupStep({ n, title, detail, done = false, active = false, action }: RunupStepProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '32px 1fr auto',
        gap: 12,
        padding: '12px 0',
        borderTop: n !== '1' ? '1px solid var(--line)' : 'none',
        alignItems: 'start',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: done ? 'var(--ok)' : active ? 'var(--primary)' : 'var(--surface-3)',
          color: done || active ? '#fff' : 'var(--ink-3)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {done ? <Icon name="check" size={14} color="#fff" /> : n}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{detail}</div>
      </div>
      <div>{action}</div>
    </div>
  );
}
